import { useEffect, useRef, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Send, Bot, User as UserIcon, Loader2, AlertTriangle, Sparkles, Phone, MessageSquare } from "lucide-react";
import useAuthStore from "../store/authStore";

const STARTERS = [
  "Where is my latest order?",
  "How long does delivery take?",
  "I need a refund for a damaged package",
  "How does the SLA penalty work?",
  "Speak to a human please",
];

function actionBadge(action) {
  const map = {
    track_order:        { color: "bg-blue-100 text-blue-700 border-blue-200", label: "Tracking" },
    initiate_refund:    { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Refund opened" },
    file_complaint:     { color: "bg-rose-100 text-rose-700 border-rose-200", label: "Complaint filed" },
    escalate_to_human:  { color: "bg-purple-100 text-purple-700 border-purple-200", label: "Escalated" },
    rate_limited:       { color: "bg-slate-200 text-slate-700 border-slate-300", label: "Rate-limited" },
    reply:              null,
  };
  return map[action];
}

export default function Help() {
  const { user } = useAuthStore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [remaining, setRemaining] = useState(20);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Welcome message
  useEffect(() => {
    setMessages([{
      role: "assistant",
      text: `Hi ${user?.name?.split(" ")[0] || "there"}! I'm PickNGo's AI support. Ask me about your orders, refunds, or anything else.`,
      meta: { action: "reply" },
    }]);
  }, [user]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/support/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setRemaining(data.rate_limit?.remaining ?? remaining - 1);
      setMessages((m) => [...m, {
        role: "assistant", text: data.reply,
        meta: { action: data.action, target_tid: data.target_tracking_id, ticket_id: data.ticket_id, escalated: data.escalated },
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: "Sorry — I hit an error. You can reach our team on support@pickngo.in or +91 80 0123 4567.",
        meta: { action: "escalate_to_human" },
      }]);
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl mx-auto h-[calc(100vh-180px)] flex flex-col" data-testid="help-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Bot className="h-7 w-7 text-[#FF6B00]" /> Help & Support
        </h1>
        <p className="text-sm text-slate-500 mt-1">Chat with our AI assistant. Need a human? Just say so.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex-1 flex flex-col overflow-hidden">
        {/* Message thread */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3" data-testid="chat-thread">
          {messages.map((m, i) => {
            const isUser = m.role === "user";
            const badge = !isUser ? actionBadge(m.meta?.action) : null;
            return (
              <div key={i} className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
                {!isUser && (
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#FF6B00] to-orange-600 flex items-center justify-center shrink-0">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${isUser ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-900 border border-slate-100"}`} data-testid={`chat-msg-${i}`}>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</p>
                  {badge && (
                    <span className={`inline-block mt-2 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase ${badge.color}`}>
                      {badge.label}
                      {m.meta?.target_tid ? ` · ${m.meta.target_tid}` : ""}
                    </span>
                  )}
                </div>
                {isUser && (
                  <div className="w-8 h-8 rounded-xl bg-slate-200 flex items-center justify-center shrink-0">
                    <UserIcon className="h-4 w-4 text-slate-600" />
                  </div>
                )}
              </div>
            );
          })}
          {sending && (
            <div className="flex gap-2 justify-start" data-testid="chat-typing">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#FF6B00] to-orange-600 flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Quick replies */}
        {messages.length <= 1 && (
          <div className="px-4 py-2 border-t border-slate-100 flex flex-wrap gap-2" data-testid="chat-starters">
            {STARTERS.map((s) => (
              <button key={s} onClick={() => send(s)}
                      className="px-3 py-1.5 text-xs font-medium rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                      data-testid={`starter-${s.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}>
                <Sparkles className="h-3 w-3 inline mr-1 text-[#FF6B00]" /> {s}
              </button>
            ))}
          </div>
        )}

        {/* Composer */}
        <div className="border-t border-slate-100 p-3 sm:p-4 flex items-center gap-2">
          <input
            type="text"
            placeholder="Type your question…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            disabled={sending || remaining <= 0}
            className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm"
            data-testid="chat-input"
          />
          <Button onClick={() => send()} disabled={sending || !input.trim() || remaining <= 0} className="bg-[#FF6B00] hover:bg-orange-600 text-white" data-testid="chat-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Footer info */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
        <span data-testid="chat-rate-limit">Daily messages left: <b className="text-slate-900">{remaining}</b> / 20</span>
        <span className="flex items-center gap-3">
          <a href="tel:+918001234567" className="inline-flex items-center gap-1 hover:underline"><Phone className="h-3 w-3" /> Call support</a>
          <a href="mailto:support@pickngo.in" className="inline-flex items-center gap-1 hover:underline"><MessageSquare className="h-3 w-3" /> Email</a>
        </span>
      </div>

      {remaining <= 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 text-xs text-rose-800 flex items-center gap-2" data-testid="chat-rate-limit-banner">
          <AlertTriangle className="h-4 w-4" /> Daily chat cap reached. Please call or email support for urgent issues.
        </div>
      )}
    </div>
  );
}
